import React from 'react';
import { useMutation } from '@apollo/client/react';
import { useStoreContext } from '../../../../App/StoreProvider';
import * as LOFAScheduleEditActions from '../../../../../store/actions/lofaScheduleEdit';
import * as PumpScheduleEditActions from '../../../../../store/actions/pumpScheduleEdit';
import { PumpScheduleSummaryObject } from '../../../../../store/types/PumpScheduleEditState';
import { UPDATE_PUMP_SCHEDULE_INFO } from '../../../../../graphql/client/mutations';
import { GET_JOB_PROFILE, GET_LOFA_SCHEDULE, GET_LOAD_SUMMARY } from '../../../../../graphql/client/queries';
import {
    GetFullJobProfile_getJobProfile_pumpScheduleSummary as PumpScheduleSummaryData,
    WellheadInput,
} from '../../../../../types/schemaTypes';
import { CustomButton } from '../../../../_common/CustomButton/CustomButton';
import ConfirmEditPumpSchedule from '../../ConfirmEditPumpSchedule';

interface PumpScheduleEditHeaderProps {
    changeEditMode: () => void;
    jobId: number;
    pumpScheduleSummaryList: PumpScheduleSummaryData[];
}

const PumpScheduleEditHeader = ({
    pumpScheduleSummaryList,
    jobId,
    changeEditMode,
}: PumpScheduleEditHeaderProps): React.ReactElement => {
    const [state, dispatch] = useStoreContext();
    const { pumpScheduleEdit } = state;

    const onCompleted = (): void => {
        dispatch(LOFAScheduleEditActions.setLofaScheduleToInitialState());
        changeEditMode();
    };

    const [updatePumpScheduleInfo] = useMutation(UPDATE_PUMP_SCHEDULE_INFO, {
        onCompleted: onCompleted,
        onError: () => console.log('Error'),
        refetchQueries: () => {
            return [
                {
                    query: GET_JOB_PROFILE,
                    variables: { jobId: jobId },
                },
                {
                    query: GET_LOFA_SCHEDULE,
                    variables: { jobId: jobId },
                },
                {
                    query: GET_LOAD_SUMMARY,
                    variables: { jobId: jobId },
                },
            ];
        },
    });

    const onEditCancel = (): void => {
        dispatch(PumpScheduleEditActions.setInitialData(pumpScheduleSummaryList, []));
        dispatch(PumpScheduleEditActions.resetFieldErrors());
        changeEditMode();
    };

    const onEditSave = (onSaveComplete: () => void): void => {
        if (pumpScheduleEdit.toBePosted !== null && pumpScheduleEdit.toBePosted !== undefined) {
            const news = pumpScheduleEdit.toBePosted.map(element => {
                if (
                    element != null &&
                    element.stageTime != null &&
                    element.stages != null &&
                    +element.stageTime > 0 &&
                    +element.stages > 0 &&
                    element.wellName != null
                ) {
                    const newWellHeadInput: WellheadInput = {
                        proppants: element.proppants
                            .filter((p: any) => {
                                return p.isDeleted && p.pumpScheduleId == null ? false : true;
                            })
                            .map((p: any) => ({
                                pumpScheduleId: p.pumpScheduleId || 0,
                                proppantId: p.proppantId,
                                proppantName: p.proppantName,
                                totalVolume: +p.totalVolume,
                                volumePerBox: +p.volumePerBox,
                                isDeleted: p.isDeleted,
                            })),
                        stages: +element.stages,
                        stageTime: +element.stageTime,
                        wellName: element.wellName,
                    };
                    return newWellHeadInput;
                }
            });

            updatePumpScheduleInfo({
                variables: {
                    input: {
                        jobId: jobId,
                        newValues: news,
                    },
                },
            }).then(() => {
                dispatch(PumpScheduleEditActions.setInitialData(pumpScheduleSummaryList, []));
                dispatch(PumpScheduleEditActions.setPumpScheduleToInitialState());
                onSaveComplete();
            });
        }
    };

    return (
        <div className="PumpSchedule-EditHeader">
            <div className="PumpSchedule-HeaderLeft">
                <i className="material-icons PumpSchedule-EditIcon">create</i>
                <span className="PumpSchedule-EditLink">Edit</span>
                <span className="PumpSchedule-TitleText">Pump Schedule Summary</span>
                <span className="PumpSchedule-TitleDivider">|</span>
                <span className="PumpSchedule-HelpText">You must fill in all of the fields</span>
            </div>
            <div className="PumpSchedule-HeaderRight">
                <div className="PumpSchedule-ButtonWrapper">
                    <CustomButton isBordered={true} title="Cancel" onClick={onEditCancel} />
                </div>
                <div className="PumpSchedule-ButtonWrapper">
                    <ConfirmEditPumpSchedule
                        onEditSave={onEditSave}
                        pumpScheduleSummaryList={pumpScheduleSummaryList as PumpScheduleSummaryObject[]}
                    ></ConfirmEditPumpSchedule>
                </div>
            </div>
        </div>
    );
};

export default PumpScheduleEditHeader;
